'use client';

import { c as _c } from "react/compiler-runtime";
import type { LogsEvent, LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';
import type { InitContext } from '@src/app/init';
import { configPublic } from '@src/config.public';
import { useSession } from '@src/hooks/useSession';
import { log } from '@src/utils/log.util';
import { getBuilderPreviewMode } from './builder.util';
const {
  allowedTracingUrls,
  applicationId,
  clientToken,
  env,
  isEnabled,
  logSampleRate,
  sessionReplaySampleRate,
  sessionSampleRate,
  site,
  service
} = configPublic.datadog;
const {
  appVersion,
  commitSHA
} = configPublic.general;
const version = commitSHA ? `${appVersion}-${commitSHA}` : appVersion;
export const AD_BLOCKED_VENDOR_HOSTS = ['bloomreach.com', 'amplitude.com', 'litix.io', 'stackadapt.com', 'google.com', 'doubleclick.net', 'googlesyndication.com', 'googleadservices.com', 'attn.tv', 'osano.com', 'sierra.chat', 'bing.com', 'juicer.io', 'adsrvr.org', 'podscribe.com', 'zeropartyforms.com', 'builder.io'];
function isAdBlockedVendorHost(url: string): boolean {
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return false;
  }
  return AD_BLOCKED_VENDOR_HOSTS.some(vendor => host === vendor || host.endsWith(`.${vendor}`));
}
export function datadogLogsBeforeSend(event: LogsEvent) {
  if (event.origin === 'network' && event.http?.status_code === 0 && isAdBlockedVendorHost(event.http.url)) {
    event.status = 'warn';
    event.message = `🚫 Blocked by ad-blocker: ${event.message}`;
  }
  return true;
}
export function useDataDog(t0) {
  const $ = _c(7);
  const {
    vercelDeploymentUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getBuilderPreviewMode();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    edit
  } = t1;
  const {
    anonymousId
  } = useSession();
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (edit) {
        return;
      }
      const configRUM = {
        applicationId,
        clientToken,
        site: site as RumInitConfiguration['site'],
        service,
        env,
        version,
        sessionSampleRate,
        sessionReplaySampleRate,
        allowedTracingUrls,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        silentMultipleInit: true
      };
      const configLogs = {
        clientToken,
        site: site as LogsInitConfiguration['site'],
        sessionSampleRate: logSampleRate,
        service,
        env,
        version,
        forwardConsoleLogs: [],
        forwardReports: "all",
        forwardErrorsToLogs: true,
        silentMultipleInit: true
      };
      log.debug("[DATADOG] Configs:", {
        configRUM,
        configLogs
      });
      if (!isEnabled) {
        return;
      }
      datadogRum.init(configRUM);
      datadogLogs.init({
        ...configLogs,
        beforeSend: datadogLogsBeforeSend
      });
      datadogRum.startSessionReplayRecording();
    };
    t3 = [edit];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== anonymousId || $[4] !== vercelDeploymentUrl) {
    t4 = () => {
      if (edit) {
        return;
      }
      datadogLogs.setGlobalContextProperty("vercelDeploymentUrl", vercelDeploymentUrl);
      if (anonymousId) {
        datadogRum.setUser({
          id: anonymousId
        });
        datadogLogs.setGlobalContextProperty("sessionId", anonymousId);
      }
    };
    t5 = [edit, anonymousId, vercelDeploymentUrl];
    $[3] = anonymousId;
    $[4] = vercelDeploymentUrl;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
}